import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import ErrorBoundary from 'components/ErrorBoundary';
import SkeletonGroup from 'components/Skeleton/SkeletonGroup';

import useCommentIdFromRouterHash from 'hooks/useCommentIdFromRouterHash';
import useInfiniteQuery from 'hooks/useInfiniteQuery';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import useMediaQuery from 'hooks/useMediaQuery';
import useRatings from 'hooks/useRatings';

import { nonNull, stripObjDuplicates } from 'lib/array';
import { getCommentsCount } from 'lib/comments';
import { sendEvent } from 'lib/gtag';
import { commentQueryKeys } from 'lib/queryKeys';
import { scrollToElement } from 'lib/utils';

import { useDomain } from 'providers/DomainProvider';

import { useCommentsRoot } from '../Root';

import COMMENT from 'constants/comment';

const CommentListItem = dynamic(() => import('./ListItem'));

const CommentListItemSkeleton = dynamic(() => import('./ListItem/Skeleton'));

const BrokenCommentFallback = dynamic(
  () => import('./ListItem/BrokenCommentFallback')
);

const COMMENTS_STALE_TIME = 20_000; // 20 seconds
const COMMENTS_LIST_HEADER_HEIGHT = 132;
const MILLISECONDS_TO_WAIT_COMMENTS_LIST_BE_MOUNTED = 800;

const CommentsList = ({
  aliasId = null,
  header = null,
  initialComments,
  canCommentsBeRecommendedAsReview = false,
  commentsCount: initialCommentsCount,
  className,
  queryFn,
}) => {
  const hashCommentRef = useRef(null);
  const commentsListComponentRef = useRef(null);
  const lastCommentIdFromUrlHash = useRef(null);
  const fetchNextPageRef = useRef(null);

  /*
    Tanstack v4 requer `initialData` no formato `{ pages, pageParams }`
    pra `useInfiniteQuery`. Passar um array cru fazia o `hasNextPage` e o
    `fetchNextPage` computarem valores inconsistentes — em algumas
    combinações o refetch on mount disparava sem necessidade e ao
    falhar (ex.: CORS no backend local) sumia com a lista inteira.
  */
  const initialData = useMemo(
    () =>
      Array.isArray(initialComments)
        ? { pages: [initialComments], pageParams: [undefined] }
        : undefined,
    [initialComments]
  );

  /*
    `initialData` sem `initialDataUpdatedAt` é tratado pelo tanstack
    como "atualizado em 0" — e `!0 === true` marca o cache como stale,
    disparando refetch on mount mesmo com `staleTime: Infinity`. Isso
    fazia o GET de comentários rodar em toda montagem e, se falhasse
    (CORS/5xx), o empty state aparecia no lugar da lista. Fixamos o
    timestamp no mount pra o cache já nascer "fresh".
  */
  const initialDataUpdatedAtRef = useRef(Date.now());
  const { isLg } = useMediaQuery();
  const { commentId: commentIdFromUrlHash } = useCommentIdFromRouterHash();
  const { domain, domainId, domainIdKey } = useDomain();
  const { isLiked, isDisliked } = useRatings({ domain, domainId });
  const {
    commentsSort,
    gaEventCategory,
    dispatchCommentsCount,
    setCommentsListEmpty,
  } = useCommentsRoot();

  /*
    `initialComments` veio do SSR ordenado pelo `commentsSort` inicial
    (na página de oferta, "relevance"). Quando o usuário troca a
    ordenação, o `queryKey` muda e o tanstack cria uma nova query.
    Se passássemos o mesmo `initialData` + `initialDataUpdatedAt` pra
    essa nova query, ela nasceria "fresh" com dados da ordenação
    ANTIGA — o sort virava no-op visual (a lista ficava igual).

    Capturamos o sort do primeiro render num ref e só consideramos os
    dados iniciais válidos enquanto o sort atual bate com aquele.
  */
  const initialSortRef = useRef(commentsSort);
  const isInitialSort = commentsSort === initialSortRef.current;

  const {
    data: fetchedComments,
    fetchNextPage: fetchNextCommentsPage,
    isSuccess: isCommentsSuccess,
    isError: isCommentsError,
    isFetchingNextPage: isFetchingNextCommentsPage,
    isInitialLoading: isInitialCommentsLoading,
    hasNextPage: hasNextCommentsPage,
  } = useInfiniteQuery({
    queryKey: commentQueryKeys[domain].list({
      [domainIdKey]: domainId,
      sortType: commentsSort,
    }),
    queryFn: async (after, { signal }) =>
      queryFn(
        {
          after,
          comment: commentIdFromUrlHash,
          limit: COMMENT.LIMIT,
          repliesLimit: COMMENT.REPLIES_LIMIT,
          sort: commentsSort,
          [domainIdKey]: domainId,
        },
        { signal }
      ),
    initialData: isInitialSort ? initialData : undefined,
    initialDataUpdatedAt: isInitialSort
      ? initialDataUpdatedAtRef.current
      : undefined,
    /**
     * The API takes at max 20 seconds to add a new comment to the actual
     * list, so if a user creates a new comment and tries to reload the
     * comments, it won't appear. We keep this list on cache or 20 seconds
     * so the user still sees newly added comments until the API does the
     * processing needed to actually return it on the GET comments list
     */
    staleTime: Infinity,
    cacheTime: COMMENTS_STALE_TIME,
    keepPreviousData: true,
    /*
      Só dispara ErrorBoundary quando NÃO há dados ainda — ou seja, na
      carga inicial sem `initialComments`. Quando a paginação falha (ou
      um refetch após já termos comentários renderizados) o erro é
      mantido em `error` pelo tanstack e os dados já carregados
      continuam visíveis; antes, qualquer erro de paginação derrubava a
      lista inteira no fallback.
    */
    useErrorBoundary: (_error, query) => !query.state.data,
    getNextPageParam: (lastComments) =>
      lastComments?.length >= COMMENT.LIMIT
        ? lastComments?.at(-1)?.commentId
        : null,
    /**
     * In specific cases, the `commentPages` can be transform as a array of
     * null, because this, we filter the `commentPages` before treat the comment
     * duplicates.
     *
     * There are edge cases where comments can be duplicated from the APIs..
     * here we make sure those won't break the list and treat comments as
     * a flat list instead of dealing with an array of pages for easier
     * rendering.
     */
    select: (pages) => stripObjDuplicates(nonNull(pages), 'commentId'),
    onSuccess: (data) => {
      dispatchCommentsCount({
        type: 'populate',
        count: getCommentsCount(
          initialCommentsCount,
          // TODO: don't know why always the first item is null when page reload
          data.pages.flat().filter(Boolean)
        ),
      });
    },
  });

  const isSuccess = isCommentsSuccess;
  const isError = isCommentsError;
  const isFetchingNextPage = isFetchingNextCommentsPage;
  const isInitialLoading = isInitialCommentsLoading;

  /*
    Quando a query falha (ex.: CORS ou 5xx em paginação) o tanstack
    pode devolver `fetchedComments` vazio mesmo tendo `initialComments`
    vindos do SSR. Fazemos fallback pros comentários iniciais pra lista
    não desaparecer da tela — o usuário continua enxergando o que já
    tinha sido renderizado antes do erro.
  */
  const comments =
    isError && fetchedComments.length === 0 && Array.isArray(initialComments)
      ? initialComments
      : fetchedComments;

  useEffect(() => {
    let timeout;

    if (lastCommentIdFromUrlHash.current !== commentIdFromUrlHash) {
      timeout = setTimeout(() => {
        if (!isSuccess || !hashCommentRef.current) {
          return;
        }

        if (!isLg) {
          scrollToElement(hashCommentRef.current.offsetTop);
          lastCommentIdFromUrlHash.current = commentIdFromUrlHash;
          return;
        }

        commentsListComponentRef.current.scroll({
          top: hashCommentRef.current.offsetTop - COMMENTS_LIST_HEADER_HEIGHT,
          left: 0,
          behavior: 'smooth',
        });
        lastCommentIdFromUrlHash.current = commentIdFromUrlHash;
      }, MILLISECONDS_TO_WAIT_COMMENTS_LIST_BE_MOUNTED);
    }

    return () => clearTimeout(timeout);
  }, [isSuccess, commentIdFromUrlHash, isLg]);

  useIntersectionObserver({
    enabled: hasNextCommentsPage,
    target: fetchNextPageRef,
    onIntersect: () => {
      sendEvent({
        action: 'load_comments_reading',
        category: gaEventCategory,
      });
      fetchNextCommentsPage();
    },
  });

  useEffect(() => {
    /*
      `isError` precisa derrubar o empty state: um fetch que falha
      (CORS, 5xx) deixa `fetchedComments` vazio por um instante, e sem
      essa checagem o app mostrava a ilustração "Faça o primeiro
      comentário" como se a oferta não tivesse comentários.
    */
    if (!isInitialLoading && isSuccess && !isError && comments.length === 0) {
      setCommentsListEmpty(true);
      return;
    }

    setCommentsListEmpty(false);
  }, [isInitialLoading, isSuccess, isError, comments]);

  // Quando não há comentários mas um `header` foi fornecido, mantemos o
  // container para que o header (ex.: card do autor + empty state da oferta)
  // continue visível. Sem header, preserva o comportamento antigo de null.
  if (isSuccess && comments.length === 0 && !header) {
    return null;
  }

  return (
    <div
      ref={commentsListComponentRef}
      className={twMerge(
        'scrollbar-light flex flex-col overflow-y-auto overflow-x-hidden pr-1 lg:pb-6',
        className
      )}
    >
      {header}
      {comments.map(
        ({
          commentDate,
          commentFixed,
          commentId,
          commentLikes,
          commentStatusName,
          commentText,
          edited,
          isReview,
          parentId,
          replies,
          userId,
          userLevel,
          userName,
          userPhoto,
          userSpecialtyName,
          userTypeName,
          userUsername,
        }) => (
          /*
            Isola o render de cada comentário em uma ErrorBoundary própria
            — comentários com `commentText` malformado (ex.: `<img src="
            <a ...">` gerado por editor quebrado) derrubavam a lista
            inteira via a boundary externa. Agora só o item quebrado cai
            no fallback e os demais seguem visíveis.
          */
          <ErrorBoundary key={commentId} fallback={BrokenCommentFallback}>
            <CommentListItem
              aliasId={aliasId}
              canBeRecommendedAsReview={canCommentsBeRecommendedAsReview}
              commentDate={commentDate}
              commentFixed={commentFixed}
              commentId={commentId}
              commentLikes={commentLikes}
              commentStatusName={commentStatusName}
              commentText={commentText}
              hashCommentRef={hashCommentRef}
              isDisliked={isDisliked(commentId)}
              isEdited={edited}
              isLiked={isLiked(commentId)}
              isReview={isReview}
              parentId={parentId}
              replies={replies}
              userId={userId}
              userLevel={userLevel}
              userName={userName}
              userPhoto={userPhoto}
              userSpecialtyName={userSpecialtyName}
              userTypeName={userTypeName}
              userUsername={userUsername}
            />
          </ErrorBoundary>
        )
      )}
      {/*
        Só mostra skeleton quando de fato está buscando mais páginas. Antes
        exibia também em `isLoading`/`isInitialLoading`, o que deixava 2
        skeletons presos abaixo da lista em ofertas que só têm uma página
        de comentários (o hidrata inicial com `initialData` entra nessas
        flags momentaneamente) — ficavam lá mesmo sem mais nada pra carregar.
      */}
      {isFetchingNextPage && hasNextCommentsPage && (
        <SkeletonGroup items={2} skeleton={CommentListItemSkeleton} />
      )}
      <div ref={fetchNextPageRef} />
    </div>
  );
};

CommentsList.propTypes = {
  aliasId: PropTypes.number,
  /**
   * Conteúdo opcional renderizado dentro do container com scroll, ANTES dos
   * comentários. Usado pela página de oferta para rolar o card do autor
   * junto com a lista.
   */
  header: PropTypes.node,
  initialComments: PropTypes.arrayOf(
    PropTypes.shape({
      canBeRecommendedAsReview: PropTypes.bool,
      commentDate: PropTypes.string.isRequired,
      commentFixed: PropTypes.bool,
      commentId: PropTypes.number.isRequired,
      commentLikes: PropTypes.number.isRequired,
      commentStatusName: PropTypes.string.isRequired,
      commentText: PropTypes.string.isRequired,
      edited: PropTypes.bool.isRequired,
      isReply: PropTypes.bool,
      isReview: PropTypes.bool,
      parentId: PropTypes.number,
      replies: PropTypes.arrayOf(PropTypes.shape()).isRequired,
      userId: PropTypes.number.isRequired,
      userLevel: PropTypes.number.isRequired,
      userName: PropTypes.string.isRequired,
      userPhoto: PropTypes.string.isRequired,
      userSpecialtyName: PropTypes.string,
      userTypeName: PropTypes.string.isRequired,
      userUsername: PropTypes.string.isRequired,
    })
  ),
  canCommentsBeRecommendedAsReview: PropTypes.bool,
  commentsCount: PropTypes.number.isRequired,
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  queryFn: PropTypes.func.isRequired,
};

export default CommentsList;
